import React, { useEffect, useState, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { m } from 'framer-motion';

import { getMarkdownComponents } from '../utils/markdownComponents';

interface HeroDescriptionProps {
  content?: string;
  styles?: any;
  bgImageDesktop?: string;
  bgImageMobile?: string;
  isDesktop?: boolean;
}

export type { HeroDescriptionProps };

const HeroDescription: React.FC<HeroDescriptionProps> = ({
  content,
  styles: _textStyles,
  bgImageDesktop: _bgImageDesktop,
  bgImageMobile: _bgImageMobile,
  isDesktop = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [hasMeasured, setHasMeasured] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState(isDesktop ? 112 : 72);
  const contentRef = useRef<HTMLDivElement>(null);

  const maxChars = isDesktop ? 260 : 120;
  const isLong = content ? content.length > maxChars : false;

  const normalizedContent = useMemo(() => {
    return content ? content.replace(/\\n/g, '\n').trim() : '';
  }, [content]);

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const displayMarkdown = normalizedContent;

  const counter = useMemo(() => ({ count: 0 }), []);
  counter.count = 0;

  const { desktopComponents, mobileComponents } = useMemo(
    () => getMarkdownComponents(isExpanded, 0, counter),
    [isExpanded, counter],
  );

  const collapsedLines = isDesktop ? 4 : 3;

  useEffect(() => {
    if (!isLong) {
      setHasOverflow(false);
      setHasMeasured(true);
      return;
    }

    const node = contentRef.current;
    if (!node) return;

    const measure = () => {
      const firstTextElement = node.querySelector('p, li');
      const lineTarget =
        firstTextElement instanceof HTMLElement ? firstTextElement : node;
      const computedStyle = window.getComputedStyle(lineTarget);
      const parsedLineHeight = Number.parseFloat(computedStyle.lineHeight);
      const parsedFontSize = Number.parseFloat(computedStyle.fontSize);
      const lineHeight = Number.isFinite(parsedLineHeight)
        ? parsedLineHeight
        : (Number.isFinite(parsedFontSize) ? parsedFontSize : 16) * 1.5;
      const nextCollapsedHeight = Math.ceil(lineHeight * collapsedLines);

      setCollapsedHeight((currentHeight) =>
        currentHeight === nextCollapsedHeight
          ? currentHeight
          : nextCollapsedHeight,
      );
      setHasMeasured(true);

      if (isExpanded) {
        return;
      }

      setHasOverflow(node.scrollHeight > nextCollapsedHeight + 1);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);

    return () => observer.disconnect();
  }, [collapsedLines, displayMarkdown, isExpanded, isLong]);

  if (!content) return null;

  const shouldManageHeight = isLong && (!hasMeasured || hasOverflow);
  const descriptionHeight = shouldManageHeight
    ? isExpanded
      ? 'auto'
      : collapsedHeight
    : undefined;
  const animatedHeight = descriptionHeight ?? 'auto';
  const heightTransition = { duration: 0.4, ease: [0.4, 0, 0.2, 1] };

  if (isDesktop) {
    return (
      <div className="relative w-full">
        <m.div
          ref={contentRef}
          className="relative overflow-hidden [overflow-anchor:none]"
          animate={{ height: animatedHeight }}
          transition={heightTransition}
          data-testid="hero-description-collapse"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={desktopComponents}
          >
            {displayMarkdown}
          </ReactMarkdown>
        </m.div>
        {hasOverflow && (
          <button
            type="button"
            aria-expanded={isExpanded}
            onClick={handleToggleExpand}
            className="text-theme-primary animate-fade-in mt-2 block w-fit cursor-pointer text-sm font-semibold hover:underline"
          >
            {isExpanded ? 'See less' : 'See more'}
          </button>
        )}
      </div>
    );
  }

  // Mobile and tablet layout keeps collapse.
  return (
    <div className="font-mulish text-text-body relative text-[16px] leading-[24px]">
      <m.div
        ref={contentRef}
        className="relative overflow-hidden [overflow-anchor:none]"
        animate={{ height: animatedHeight }}
        transition={heightTransition}
        data-testid="hero-description-collapse"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={mobileComponents}
        >
          {displayMarkdown}
        </ReactMarkdown>
      </m.div>
      {hasOverflow && (
        <button
          type="button"
          aria-expanded={isExpanded}
          onClick={handleToggleExpand}
          className="text-theme-primary mt-2 block w-fit cursor-pointer text-sm font-semibold hover:underline"
        >
          {isExpanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  );
};

export default HeroDescription;
